import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DateTime } from 'luxon';
import { DetectMethodChanges, isValidNumber, isVoid } from '../../utils';
import { COPILOT_DISCLAIMER } from '../../copilot-shared/copilot-disclaimer';
import { COPILOT_DISCLAIMER_ENABLED } from '../../copilot-shared/copilot-disclaimer-visibility';
import { RollSuggestion } from './apg-copilot-domain';

// One Roll Positions suggestion, rendered like a vending-machine card: collapsed summary that
// expands for detail. ADVISORY ONLY — actions are Preview + Review (no Apply, no legs on the card).
//
// Default change detection (NOT OnPush): the parent mutates suggestion.state.previewed in place
// then runs detectChanges(); an OnPush card would skip the in-place mutation.
@Component({
    selector: 'ets-apg-copilot-snippet-card',
    templateUrl: './apg-copilot-snippet-card.component.html',
    styleUrls: ['./apg-copilot-snippet-card.component.scss']
})
export class ApgCopilotSnippetCardComponent implements OnInit, OnDestroy {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {}

    // M8: the shared compliance disclaimer, rendered under the action row. A constant, never
    // reassigned — exposed as a field only because templates cannot import.
    readonly disclaimer = COPILOT_DISCLAIMER;

    // CR-20260723-003: global visibility switch for the disclaimer above (client-requested disable).
    // HIDDEN, not removed - flip COPILOT_DISCLAIMER_ENABLED to re-show the disclaimer at all five sites.
    readonly disclaimerEnabled = COPILOT_DISCLAIMER_ENABLED;

    @Input() suggestion: RollSuggestion;

    // 0-based rank from the results list; rendered 1-based as "Adjustment #N".
    @Input() index = 0;

    @Output() previewToggle = new EventEmitter<RollSuggestion>();
    @Output() review = new EventEmitter<RollSuggestion>();

    // Cards arrive collapsed; clicking the header toggles the full detail.
    expanded = false;

    // Live cost refresh. The card shows a LIVE adjustment price (costText -> content.livePrice() ->
    // PriceboxColumn.price, mutated in place as quotes arrive). This card sits under an OnPush results-list whose
    // [suggestions] input reference is STABLE while prices tick, so OnPush skips the subtree and NO parent-level
    // detectChanges() ever reaches this card (that is why three orchestrator-level refreshes all failed). So,
    // exactly like the Price Box cell, the card drives its OWN change detection: an in-zone interval (ngOnInit
    // runs in-zone) re-checks THIS card's view, and its own detectChanges() ignores the ancestor OnPush gate.
    private _priceRefreshTimer: any = null;

    ngOnInit(): void {
        this._priceRefreshTimer = setInterval(() => this._changeDetector.detectChanges(), 500);
    }

    ngOnDestroy(): void {
        if (this._priceRefreshTimer !== null) {
            clearInterval(this._priceRefreshTimer);
            this._priceRefreshTimer = null;
        }
    }

    get previewed(): boolean {
        return !!this.suggestion && this.suggestion.state.previewed;
    }

    get previewLabel(): string {
        return this.previewed ? 'Un-Preview' : 'Preview';
    }

    // Cards are named by rank now, not by date (owner 2026-07-07): "Adjustment #1", "#2", …
    get cardNumber(): string {
        return 'Adjustment #' + (this.index + 1);
    }

    // The expiration date, canonical across every CoPilot: "Wed 08-Jul-26" (weekday derived + dd-MMM-yy).
    get expirationText(): string {
        const c = this.suggestion.content;
        const dow = this.dayOfWeek(c.adjustmentDate);
        return (dow ? dow + ' ' : '') + c.adjustmentDate;
    }

    // Header row 2, right side: the canonical date + DTE badge, e.g. "Wed 08-Jul-26 (1d)".
    get dateText(): string {
        return this.expirationText + ' (' + this.suggestion.content.daysOut + 'd)';
    }

    // Derive the weekday from the pretty expiration date ("dd-MMM-yy"); null if unparseable.
    private dayOfWeek(pretty: string): string | null {
        const dt = DateTime.fromFormat(pretty, 'dd-MMM-yy');
        return dt.isValid ? dt.weekdayShort : null;
    }

    get typeLabel(): string {
        return this.suggestion.content.adjustmentTypes.join(' + ');
    }

    get strikePath(): string {
        const c = this.suggestion.content;
        return c.currentStrike + ' → ' + c.destinationStrike;
    }

    // Live signed cell price (quote-driven PriceboxColumn.price), or null when unavailable/unpriced so the
    // caller falls back to the run-time snapshot. Mirrors the Price Box cell, which re-reads the live price on
    // each CD tick rather than caching it. This card's own ngOnInit interval (above) drives that CD.
    private liveSignedCost(): number | null {
        const c = this.suggestion.content;
        if (!c.livePrice) {
            return null;
        }
        const p = c.livePrice();
        return isValidNumber(p) ? p : null;
    }

    get isCredit(): boolean {
        const live = this.liveSignedCost();
        if (live !== null) {
            return live >= 0;   // credit<->debit flips live if the market crosses zero
        }
        return this.suggestion.content.costDirection === 'credit';
    }

    // "+$23.85" (credit, green) / "−$2.17" (debit, red). Live when a quote-driven price is available, else
    // the run-time snapshot. The sign encodes the direction and updates in real-time like the Price Box.
    get costText(): string {
        const live = this.liveSignedCost();
        if (live !== null) {
            return (live >= 0 ? '+$' : '−$') + Math.abs(live).toFixed(2);
        }
        const c = this.suggestion.content;
        const sign = c.costDirection === 'credit' ? '+$' : '−$';
        return sign + c.cost.toFixed(2);
    }

    // ---- The reason line (CR-20260904-001, REQ-8) ---------------------------------------------------
    // The demo showed a card whose header read +$32.43 while its own reason line said $32.38: the header
    // is LIVE, the reason was a sentence frozen by the skill at run time, and the market moved between
    // the two. So the skill now ships the reason's PARTS and the card composes the sentence here, around
    // the SAME liveSignedCost() the header reads. One source: the two figures cannot drift apart, and a
    // credit -> debit flip moves both together.
    //
    // A skill that ships no parts (the mock) falls back to its frozen `summary` - unchanged behaviour.

    get reasonText(): string {
        const c = this.suggestion.content;
        const reason = c.reason;
        if (!reason) {
            return c.summary;
        }
        const money = (this.isCredit ? 'Collects a $' : 'Pays a $')
            + this.reasonAmountText
            + (this.isCredit ? ' Credit' : ' Debit');
        return reason.lead + money + reason.tail;
    }

    // The bare amount ("32.38"), from the same accessor and the same fallback as costText.
    private get reasonAmountText(): string {
        const live = this.liveSignedCost();
        const amount = live !== null ? Math.abs(live) : this.suggestion.content.cost;
        return amount.toFixed(2);
    }

    // ---- Spread offset / spread width ---------------------------------------------------------------
    // Surfaced VERBATIM from the SAME CashFlowAdjustment fields the Price Box shows (actualSpreadOffset /
    // actualSpreadWidth) so the card and the grid can never disagree. A Calls&Puts cell couples two
    // adjustments: [0] = call side ("first"), [1] = put side ("second") -> rendered "$call/$put". These are
    // STATIC strike geometry (offset = short strike -> long leg; width = long leg -> short leg), NOT
    // quote-driven, so a plain snapshot read is correct (no live accessor / self-CD like the cost).

    // Orange indicator: "$50/$50" (both sides) | "$50" (single-sided) | null (no data -> row hidden).
    get spreadOffsetText(): string | null {
        return this.pairText(this.offsetAmount(0), this.offsetAmount(1));
    }

    // Blue indicator, same shape as the offset.
    get spreadWidthText(): string | null {
        return this.pairText(this.widthAmount(0), this.widthAmount(1));
    }

    // Gate the whole offsets row: hidden when neither indicator has data (mock/demo/custom cells whose
    // adjustments carry null offsets).
    get hasOffsets(): boolean {
        return this.spreadOffsetText !== null || this.spreadWidthText !== null;
    }

    private offsetAmount(side: number): number | null {
        return this.actualAmount(side, 'offset');
    }

    private widthAmount(side: number): number | null {
        return this.actualAmount(side, 'width');
    }

    private actualAmount(side: number, which: 'offset' | 'width'): number | null {
        const adjustments = this.suggestion.content.adjustments;
        if (isVoid(adjustments) || side >= adjustments.length) {
            return null;
        }
        const adjustment = adjustments[side];
        const value = which === 'offset' ? adjustment.actualSpreadOffset : adjustment.actualSpreadWidth;
        return isValidNumber(value) ? value : null;
    }

    // "$call/$put", or "$call"/"$put" when only one side is valid, or null when neither is. Raw numbers
    // (no decimals) to match the Price Box, since offsets are whole strike distances.
    private pairText(first: number | null, second: number | null): string | null {
        const parts: string[] = [];
        if (first !== null) {
            parts.push('$' + first);
        }
        if (second !== null) {
            parts.push('$' + second);
        }
        return parts.length ? parts.join('/') : null;
    }

    @DetectMethodChanges()
    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }

    onPreview(): void {
        this.previewToggle.emit(this.suggestion);
    }

    onReview(): void {
        this.review.emit(this.suggestion);
    }
}
